import type { PlanTier } from '@super/shared-types'
import { cn } from '@/lib/utils'
import { TIER_SELECTED_CLASSES } from '../tierColors'

interface PlanTierCardProps {
  tier: PlanTier
  selected: boolean
  recommended: boolean
  onSelect: () => void
}

const REPORTING_LABEL: Record<
  PlanTier['features']['activityReporting'],
  string
> = {
  basic: 'Basic activity reporting',
  analytics: 'Advanced activity reporting',
  custom: 'Custom activity reporting',
}

const SUPPORT_LABEL: Record<PlanTier['features']['supportLevel'], string> = {
  community: 'Community support',
  email: 'Email support',
  priority: 'Priority support',
  dedicated: 'Dedicated success mgr',
}

export function PlanTierCard({
  tier,
  selected,
  recommended,
  onSelect,
}: PlanTierCardProps) {
  const featureBullets = [
    tier.features.sdkAccess ? 'SDK access' : null,
    REPORTING_LABEL[tier.features.activityReporting],
    SUPPORT_LABEL[tier.features.supportLevel],
    tier.features.mcpDataAccess ? 'Advanced MCP data' : null,
    tier.features.realtimeWebhooks ? 'Realtime webhooks' : null,
    tier.features.builderProgramUpdates ? 'Super builder updates' : null,
  ].filter(Boolean) as string[]

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'relative flex w-full flex-col gap-2 rounded-lg border p-3 text-left transition-all duration-200',
        'hover:-translate-y-0.5 active:scale-[0.99]',
        selected
          ? cn(TIER_SELECTED_CLASSES[tier.id], 'ring-2 shadow-sm')
          : 'border-border bg-background hover:border-foreground/40',
      )}
    >
      {recommended ? (
        <span className="bg-foreground text-background font-fragment-mono absolute -top-2 right-3 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.12em]">
          Best fit
        </span>
      ) : null}

      <div className="flex items-baseline justify-between gap-2">
        <span className="font-space-grotesk text-base font-semibold tracking-tight">
          {tier.label}
        </span>
        <span className="font-space-grotesk text-lg font-semibold">
          ${tier.priceUsd}
          <span className="text-muted-foreground text-[10px] font-normal">
            /mo
          </span>
        </span>
      </div>

      <div className="text-muted-foreground grid grid-cols-2 gap-x-2 text-[11px]">
        <span>{tier.includedMinutes.toLocaleString()} min</span>
        <span className="text-right">
          ${tier.perMinuteUsd.toFixed(2)}/min over
        </span>
        <span>{tier.includedSms.toLocaleString()} SMS</span>
        <span className="text-right">
          ${tier.perSmsUsd.toFixed(2)}/SMS over
        </span>
      </div>

      <ul className="text-muted-foreground flex flex-col gap-0.5 text-[11px]">
        {featureBullets.map(b => (
          <li key={b}>· {b}</li>
        ))}
      </ul>
    </button>
  )
}
